/**
 * AdminShell — auth context, adminApi axios helper, LoginPage, Sidebar, AdminLayout.
 * Extracted from AdminPanel.js to keep the main file focused on routing.
 */
import React, { createContext, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, Package, Lightbulb, FileText, LogOut, Eye, Loader2,
  Settings, BarChart3, Mail, MessageCircle, Globe, UserCog, Newspaper, Sparkles, Menu,
} from 'lucide-react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export const adminApi = (token) =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

/** Shared input classname (used across admin forms). */
export const ic =
  'w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#0EA5E9]/30';

// ===== LOGIN =====
export const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      onLogin(res.data);
      navigate('/admin');
    } catch {
      toast.error('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#050E1F] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne,sans-serif' }}>ALLCOM Admin</h1>
          <p className="text-sm text-slate-400 mt-1">Panel de administración</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 rounded-2xl bg-white shadow-2xl space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@allcom.cl" className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0EA5E9]/30 focus:border-[#0EA5E9] outline-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Contraseña</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0EA5E9]/30 focus:border-[#0EA5E9] outline-none" />
          </div>
          <button type="submit" disabled={loading} className="w-full py-3 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 disabled:opacity-50" data-testid="admin-login-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Iniciar sesión'}
          </button>
        </form>
        <p className="text-xs text-slate-500 text-center mt-4">
          <Link to="/" className="text-[#0EA5E9] hover:underline">← Volver al sitio</Link>
        </p>
      </div>
    </div>
  );
};

// ===== SIDEBAR =====
const Sidebar = ({ active }) => {
  const auth = useAuth();
  const items = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/admin' },
    { id: 'menu-builder', label: 'Menú Principal', icon: Menu, href: '/admin/menu-builder' },
    { id: 'leads', label: 'Leads', icon: Users, href: '/admin/leads' },
    { id: 'team', label: 'Equipo / Responsables', icon: Eye, href: '/admin/team' },
    { id: 'products', label: 'Productos', icon: Package, href: '/admin/products' },
    { id: 'hw-categories', label: 'Categorías Hardware', icon: Package, href: '/admin/hw-categories' },
    { id: 'solutions', label: 'Soluciones', icon: Lightbulb, href: '/admin/solutions' },
    { id: 'cases', label: 'Casos', icon: FileText, href: '/admin/cases' },
    { id: 'crew', label: 'Crew / Equipo Web', icon: Users, href: '/admin/crew' },
    { id: 'kian-news', label: 'Kian Daily · Noticias', icon: Newspaper, href: '/admin/kian-news' },
    { id: 'releases', label: 'Releases (Novedades)', icon: Sparkles, href: '/admin/releases' },
    { id: 'newsletter', label: 'Suscriptores Newsletter', icon: Mail, href: '/admin/newsletter' },
    { id: 'site-config', label: 'Landing / Hero', icon: Settings, href: '/admin/site-config' },
    { id: 'content', label: 'Contenido del Sitio', icon: Globe, href: '/admin/content' },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, href: '/admin/whatsapp' },
    { id: 'email-config', label: 'Email / Correos', icon: Mail, href: '/admin/email-config' },
    { id: 'demo-kpis', label: 'KPIs Demos', icon: BarChart3, href: '/admin/demo-kpis' },
    { id: 'admin-users', label: 'Usuarios Admin', icon: UserCog, href: '/admin/users' },
  ];
  return (
    <aside className="w-64 bg-[#0A2540] min-h-screen text-white flex flex-col shrink-0" data-testid="admin-sidebar">
      <div className="p-4 border-b border-white/10">
        <Link to="/admin" className="flex items-center gap-2">
          <span className="font-bold text-lg" style={{ fontFamily: 'Syne,sans-serif' }}>ALLCOM</span>
          <span className="text-[10px] px-2 py-0.5 bg-[#0EA5E9]/20 text-[#0EA5E9] rounded-full font-semibold">ADMIN</span>
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.map((item) => (
          <Link
            key={item.id}
            to={item.href}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
              active === item.id
                ? 'bg-white/10 text-white font-medium'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="text-xs text-slate-400 mb-2 px-3">{auth?.email}</div>
        <button onClick={auth?.logout} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/5 rounded-lg w-full">
          <LogOut className="w-4 h-4" />Cerrar sesión
        </button>
      </div>
    </aside>
  );
};

export const AdminLayout = ({ children, active }) => (
  <div className="flex min-h-screen bg-[#F8FAFC]">
    <Sidebar active={active} />
    <main className="flex-1 p-6 lg:p-8 overflow-y-auto">{children}</main>
  </div>
);
